import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { ROUTES, CONFIG } from "../constants";
import store from "../redux/store";
// Imported from the concrete module rather than the `redux/actions` barrel:
// the barrel re-exports `auth.actions`, which imports the services back, and
// the resulting cycle is easy to trip over.
import { finishLoading, startLoading } from "../redux/actions/spinner.actions";
import { getToken } from "../redux/selectors";
import { AuthService } from "./auth.service";

const http = axios.create({
  baseURL: `${CONFIG.API_SERVER}/`,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'x-csrftoken',
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
  },
});

const httpResponseHandler = (response: AxiosResponse) => {
  return response.data;
};

/**
 * Returned when the browser is being navigated away from this document.
 *
 * `return`ing plain `undefined` from this rejection handler RESOLVES the
 * promise, so every caller's success path runs against `undefined` during the
 * few hundred milliseconds before the navigation commits -- `.then(data =>
 * data.results)` throws, and the user sees a crash toast on top of a screen
 * they should never have been shown (SD-4350).
 *
 * Never settling is the same device this file already uses for a deliberately
 * cancelled request, and for the same two reasons: the success path must not
 * run, and rejecting would surface as an unhandled rejection in every caller
 * that fetches without a `.catch`.
 *
 * It does not leak, which is the fair objection to a module-level promise that
 * retains everything chained to it. Both `return` sites sit immediately after a
 * `window.location.href` assignment, so a document navigation always follows
 * and the retention ends at unload. A caller passing `noAuthErrRouting` never
 * reaches this branch at all -- it falls through to `throw err` below and
 * rejects normally, so nothing is silently swallowed for those.
 */
const NEVER_SETTLES = new Promise<never>(() => {});

const httpErrorHandler = (err: AxiosError, extra: any = {}) => {
  const response = err?.response;

  if (!extra.noAuthErrRouting && response?.status === 401) {
    //@ts-ignore
    if (response?.data?.code === "totp_session_required"
      //@ts-ignore
      || response?.data?.code === "totp_code_mismatch"
      //@ts-ignore
      || response?.data?.code === "totp_jwt_decoding_error"
      //@ts-ignore
      || response?.data?.code === "totp_jwt_invalid"
      //@ts-ignore
      || response?.data?.code === "totp_not_setup") {
      window.location.href = ROUTES.AUTH.CONFIRM_TOTP;
      return NEVER_SETTLES;
    } else {
      window.location.href = CONFIG.API_LOGIN;
      return NEVER_SETTLES;
    }
  }
  throw err;
};

// Every in-flight request registers its controller here so we can abandon the
// whole set at once. Without this, a slow request issued under organization A
// can resolve after the user has switched to organization B and write A's data
// into B's screen.
const pendingControllers = new Set<AbortController>();

export const abortPendingRequests = () => {
  pendingControllers.forEach((controller) => controller.abort());
  pendingControllers.clear();
};

export const setHeaders = headers => {
  http.defaults.headers.common = {
    ...http.defaults.headers.common,
    ...headers,
  }
}

export class HttpService {
  static head(url: string, params: any = {}, headers: any = {}, hasSpinner = true, extra={}) {
    return HttpService.request('HEAD', url, { params, headers }, hasSpinner, extra);
  }

  static get(url: string, params: any = {}, headers: any = {}, hasSpinner = true, extra={}) {
    return HttpService.request('GET', url, { params, headers }, hasSpinner, extra);
  }

  static post(url: string, body: any = {}, headers: any = {}, hasSpinner = true, extra={}) {
    return HttpService.request('POST', url, { data: body, headers }, hasSpinner, extra);
  }

  static put(url: string, body: any = {}, headers: any = {}, hasSpinner = true, extra={}) {
    return HttpService.request('PUT', url, { data: body, headers }, hasSpinner, extra);
  }

  static patch(url: string, body: any = {}, headers: any = {}, hasSpinner = true, extra={}) {
    return HttpService.request('PATCH', url, { data: body, headers }, hasSpinner, extra);
  }

  static delete(url: string, body: any = {}, headers: any = {}, hasSpinner = true, extra={}) {
    return HttpService.request('DELETE', url, { data: body, headers }, hasSpinner, extra);
  }

  static request(method: Method, url: string, options: AxiosRequestConfig, hasSpinner = true, extra={}): Promise<any> {
    if (hasSpinner) {
      store.dispatch(startLoading());
    }

    const controller = new AbortController();
    pendingControllers.add(controller);

    const settle = () => {
      pendingControllers.delete(controller);
      // Only release the spinner if this request took it, otherwise background
      // (`hasSpinner = false`) calls drain the shared counter and hide a
      // spinner they never raised.
      if (hasSpinner) {
        store.dispatch(finishLoading());
      }
    };

    return http
      .request({
        ...options,
        method,
        url,
        signal: options.signal ?? controller.signal,
      })
      .then(
        response => {
          settle();
          return httpResponseHandler(response);
        },
        err => {
          settle();
          if (axios.isCancel(err)) {
            // Abandoned on purpose (e.g. an organization switch). Deliberately
            // never settle: the caller's success path must not run, and
            // rejecting would surface as an unhandled rejection in every caller
            // that fetches without a `.catch`.
            return new Promise<never>(() => {});
          }
          return httpErrorHandler(err, extra);
        },
      );
  };
}
